import { FC, Fragment, useCallback } from 'react';
import { Controller, useFormContext, useFieldArray, useWatch } from 'react-hook-form';
// Icons
import { MdDragIndicator } from 'react-icons/md';
// i18next
import { useTranslation } from 'react-i18next';
// Types
import QuestionTypes from 'types/QuestionTypes';
// Components
import { Button } from 'components/Buttons';
import { Checkbox, Select } from 'components/Controls';
import { IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
import Icon from 'components/Icon';
import Answer from './Answer';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
// Services
import QuestionService from 'services/question.service';
// Utilities
import { isRequired } from 'utilities/validation';
// framer-motion
import { Reorder } from 'framer-motion/dist/framer-motion';
import ImageUpload from 'components/ImageUpload';
import MediaFormats from 'types/MediaFormats';

type Props = {
  onRemove: ((questionIndex: number, questionRef: string) => void) | undefined;
  questionRef: string;
  index: number;
};

const Question:FC<Props> = ({ questionRef, index, onRemove }) => {
  const { t } = useTranslation();

  const { control, watch, setValue, formState: { errors } } = useFormContext();

  const watchedQuestions = watch('questions');
  const questionIndex = watchedQuestions.findIndex((question:any) => question.ref === questionRef);

  const { fields, append, remove } = useFieldArray<any>({
    control,
    name: `questions[${questionIndex}].answers`
  });

  const handleAdd = () => {
    append(QuestionService.initialAnswer([]));
  }

  const handleDelete = (index:number) => {
    remove(index);
  }

  const watchedItems = useWatch({
    control,
    name: `questions[${questionIndex}].answers`
  });

  const reorder = (newItems:any) => {
    setValue(`questions[${questionIndex}].answers`, newItems);
  }

  const getQuestionError = useCallback((field:string) => {
    if ( !errors || !errors.questions ) return '';

    const question = (errors as any).questions[questionIndex];

    return question && question[field] ? question[field].message : '';
    // eslint-disable-next-line
  }, [errors]);

  const watchedQuestionType = watch(`questions[${questionIndex}].type`);

  return (
    <div className="flex flex-wrap -mx-6 -y-4 px-6 pb-3">
      <div className="w-full landscape:w-1/2 tablet:w-full laptop:w-7/12 px-6">
        <div className="flex items-center gap-4 mb-4">
          <h2 className="flex-grow text-white text-xl leading-4 font-bold py-3">{t('labels.question')} {index + 1}</h2>
          {onRemove !== undefined && (
            <Tooltip title={t('buttons.removeQuestion')}>
              <IconButton
                onClick={() => onRemove(questionIndex, questionRef)}
              ><Icon icon="delete" /></IconButton>
            </Tooltip>
          )}
        </div>

        <MultiLanguageTextField
          rules={{ required: isRequired }}
          name={`questions.${questionIndex}.title`}
          customKey={`questions.${questionRef}.title`}
          label={t('labels.question')}
          className="mb-4"
          required={true}
          error={Boolean(getQuestionError('title'))}
          errorText={getQuestionError('title')}
        />

        <Controller
          control={control} name={`questions.${questionIndex}.type`}
          render={({ field }) => (
            <Select
              {...field}
              label={t('labels.type')} id={`questions.${questionIndex}.type`}
              options={QuestionService.questionTypeOptions()}
            />
          )}
        />

        {watchedQuestionType === QuestionTypes.SingleChoice && (
          <Controller
            control={control} name={`questions.${questionIndex}.renderAsDropdown`}
            render={({ field: { value, ...otherFields } }) => (
              <Checkbox
                {...otherFields}
                className="mb-0"
                label={t('labels.renderAsDropdown')}
                id={`questions.${questionIndex}.renderAsDropdown`}
                checked={value}
              />
            )}
          />
        )}

        {watchedQuestionType !== QuestionTypes.ShortText ? (
          <Fragment>
            <span className="divider"></span>
            <h2 className="text-white text-xl font-bold leading-6 my-4">{t('labels.answers')}</h2>

            <Reorder.Group axis="y" values={watchedItems} as="div" onReorder={reorder}>
              {watchedItems.map((item:any, index:number) => (
                <Reorder.Item
                  key={`${questionIndex}-answer-item-${item.ref}`} as="div"
                  className="flex items-center gap-4"
                  value={item}
                >
                  <IconButton onClick={() => {}}>
                    <MdDragIndicator />
                  </IconButton>
                  <Answer
                    questionIndex={questionIndex}
                    questionRef={questionRef}
                    index={index}
                    onDelete={fields.length > 2 ? handleDelete : undefined}
                  />
                </Reorder.Item>
              ))}
            </Reorder.Group>

            <div className="text-right mb-4">
              <Button
                onClick={handleAdd}
                variant="contained"
              >{t('buttons.addAnswer')}</Button>
            </div>
          </Fragment>
        ) : null}
        <span className="divider my-4" />
      </div>
      <div
        className="
          w-full landscape:w-1/2 tablet:w-full laptop:w-5/12
          landscape:border-l tablet:border-l-0 laptop:border-l
          px-6
        "
        style={{
          borderColor: 'rgba(255,255,255,0.2)'
        }}
      >
        <Controller
          control={control}
          name={`questions.${questionIndex}.image`}
          render={({ field }) => (
            <ImageUpload
              {...field}
              format={MediaFormats.Question}
              label="labels.image"
            />
          )}
        />
      </div>
    </div>
  )
}

export default Question;